import { Component, Suspense, memo, useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { AppIcon } from '../brand/brand';
import { APPS, type AppId } from './apps';
import { APP_COMPONENTS } from './appComponents';
import { clampPosition, resizeBounds, type Bounds, type Edge } from './geometry';
import { DUR, EASE, cancelMotion, play } from './motion';
import { activeProcessesFor, useProcesses } from './processes';
import { useWm } from './wm';
import { hasUnsavedChanges } from './unsaved';

class Boundary extends Component<{ title: string; children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) {
    return { error };
  }
  componentDidCatch(error: Error) {
    console.error(error);
  }
  render() {
    if (this.state.error)
      return (
        <div className="win-crash" role="alert">
          {this.props.title} stopped because of an error: {this.state.error.message}
          {'\n\n'}
          <button className="btn sm" onClick={() => this.setState({ error: null })}>
            Reload app
          </button>
        </div>
      );
    return this.props.children;
  }
}

const EDGES: Edge[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

function taskbarRect(id: AppId): DOMRect | null {
  const el = document.querySelector(`[data-task="${id}"]`) ?? document.querySelector('[data-switcher]');
  if (!el) return null;
  const r = el.getBoundingClientRect();
  return r.width ? r : null;
}

function toTaskbarKeyframes(win: HTMLElement, id: AppId): Keyframe[] {
  const r = win.getBoundingClientRect();
  const t = taskbarRect(id);
  const tx = t ? t.left + t.width / 2 - (r.left + r.width / 2) : 0;
  const ty = t ? t.top + t.height / 2 - (r.top + r.height / 2) : r.height / 2;
  const s = t ? Math.max(0.08, Math.min(0.25, t.width / r.width)) : 0.2;
  return [
    { transform: 'translate(0,0) scale(1)', opacity: 1 },
    { transform: `translate(${tx}px, ${ty}px) scale(${s})`, opacity: 0 },
  ];
}

function WindowFrame({ id }: { id: AppId }) {
  const win = useWm((s) => s.wins[id]);
  const focused = useWm((s) => s.focused === id);
  const phone = useWm((s) => s.phone);
  const busy = useProcesses((s) => Object.values(s.procs).some((p) => p.app === id && p.active));
  const ref = useRef<HTMLElement>(null);
  const [hidden, setHidden] = useState(false); // minimized and its animation finished
  const lastRect = useRef<DOMRect | null>(null);
  const meta = APPS[id];

  // ---- open
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (win?.minimized) {
      setHidden(true);
      return;
    }
    void play(el, [{ opacity: 0, transform: 'scale(0.97) translateY(6px)' }, { opacity: 1, transform: 'none' }], {
      duration: DUR.open,
      easing: EASE.out,
    });
    if (!el.contains(document.activeElement)) el.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- close
  const phase = win?.phase;
  useEffect(() => {
    const el = ref.current;
    if (phase !== 'closing' || !el) return;
    void play(el, [{ opacity: 1, transform: 'none' }, { opacity: 0, transform: 'scale(0.96)' }], {
      duration: DUR.close,
      easing: EASE.in,
      fill: 'forwards',
    }).then(() => useWm.getState().remove(id));
    // Safety net: if the animation is cancelled, still remove the window.
    const t = setTimeout(() => useWm.getState().remove(id), DUR.close + 400);
    return () => clearTimeout(t);
  }, [phase, id]);

  // ---- minimize / restore (travels to and from the actual taskbar button)
  const minimized = !!win?.minimized;
  const firstMin = useRef(true);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (firstMin.current) {
      firstMin.current = false;
      return;
    }
    cancelMotion(el); // measure the untransformed window, not a half-finished animation
    if (minimized) {
      setHidden(false);
      void play(el, toTaskbarKeyframes(el, id), { duration: DUR.minimize, easing: EASE.in, fill: 'forwards' }).then((done) => {
        if (done && useWm.getState().wins[id]?.minimized) {
          el.classList.add('minimized'); // hide before dropping the fill so nothing flashes
          setHidden(true);
          cancelMotion(el);
        }
      });
    } else {
      setHidden(false);
      const frames = toTaskbarKeyframes(el, id).reverse();
      void play(el, frames, { duration: DUR.minimize, easing: EASE.out }).then((done) => done && cancelMotion(el));
    }
  }, [minimized, id]);

  // ---- maximize / restore (coordinated bounds transition)
  const maximized = !!win?.maximized;
  const firstMax = useRef(true);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const parent = el.parentElement?.getBoundingClientRect();
    const now = el.getBoundingClientRect();
    if (firstMax.current || phone) {
      firstMax.current = false;
      lastRect.current = now;
      return;
    }
    const from = lastRect.current;
    lastRect.current = now;
    if (!from || !parent) return;
    void play(
      el,
      [
        { left: `${from.left - parent.left}px`, top: `${from.top - parent.top}px`, width: `${from.width}px`, height: `${from.height}px` },
        { left: `${now.left - parent.left}px`, top: `${now.top - parent.top}px`, width: `${now.width}px`, height: `${now.height}px` },
      ],
      { duration: DUR.maximize, easing: EASE.inout },
    ).then((done) => done && cancelMotion(el));
  }, [maximized, phone]);

  // Keep lastRect fresh after drags/resizes so the next maximize starts from the right place.
  const bounds = win?.bounds;
  useLayoutEffect(() => {
    if (ref.current && !maximized) lastRect.current = ref.current.getBoundingClientRect();
  }, [bounds, maximized]);

  // ---- drag
  const onTitlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.button !== 0 || (e.target as HTMLElement).closest('button') || useWm.getState().phone) return;
      const el = ref.current;
      const st = useWm.getState();
      const w = st.wins[id];
      if (!el || !w) return;
      const handle = e.currentTarget as HTMLElement;
      handle.setPointerCapture(e.pointerId);
      const sx = e.clientX;
      const sy = e.clientY;
      let start = { ...w.bounds };
      let wasMax = w.maximized;
      let moved = false;
      let raf = 0;
      let nx = start.x;
      let ny = start.y;
      const area = st.area;
      const apply = () => {
        raf = 0;
        el.style.left = nx + 'px';
        el.style.top = ny + 'px';
      };
      const move = (ev: PointerEvent) => {
        const dx = ev.clientX - sx;
        const dy = ev.clientY - sy;
        if (!moved) {
          if (Math.abs(dx) + Math.abs(dy) < 4) return;
          moved = true;
          cancelMotion(el);
          el.classList.add('dragging');
          if (wasMax) {
            // Pull a maximised window off the edge: keep the pointer at the same relative spot.
            const frac = (sx - el.getBoundingClientRect().left) / el.getBoundingClientRect().width;
            const desk = el.parentElement!.getBoundingClientRect();
            start = { ...start, x: sx - desk.left - start.w * frac, y: 0 };
            useWm.getState().setBounds(id, start);
            wasMax = false;
          }
        }
        const p = clampPosition(start.x + dx, start.y + dy, start.w, area);
        nx = Math.round(p.x);
        ny = Math.round(p.y);
        if (!raf) raf = requestAnimationFrame(apply);
      };
      const up = () => {
        handle.removeEventListener('pointermove', move);
        handle.removeEventListener('pointerup', up);
        handle.removeEventListener('pointercancel', up);
        if (raf) cancelAnimationFrame(raf);
        el.classList.remove('dragging');
        if (moved) {
          apply();
          useWm.getState().setBounds(id, { ...start, x: nx, y: ny });
        }
      };
      handle.addEventListener('pointermove', move);
      handle.addEventListener('pointerup', up);
      handle.addEventListener('pointercancel', up);
    },
    [id],
  );

  // ---- resize
  const onResizeDown = useCallback(
    (edge: Edge) => (e: React.PointerEvent) => {
      if (e.button !== 0) return;
      e.stopPropagation();
      const el = ref.current;
      const st = useWm.getState();
      const w = st.wins[id];
      if (!el || !w || w.maximized) return;
      st.focus(id);
      const handle = e.currentTarget as HTMLElement;
      handle.setPointerCapture(e.pointerId);
      cancelMotion(el);
      el.classList.add('resizing');
      const start = { ...w.bounds };
      const sx = e.clientX;
      const sy = e.clientY;
      let next: Bounds = start;
      let raf = 0;
      const apply = () => {
        raf = 0;
        Object.assign(el.style, { left: next.x + 'px', top: next.y + 'px', width: next.w + 'px', height: next.h + 'px' });
      };
      const move = (ev: PointerEvent) => {
        next = resizeBounds(start, edge, ev.clientX - sx, ev.clientY - sy, meta.minW, meta.minH, st.area);
        if (!raf) raf = requestAnimationFrame(apply);
      };
      const up = () => {
        handle.removeEventListener('pointermove', move);
        handle.removeEventListener('pointerup', up);
        handle.removeEventListener('pointercancel', up);
        if (raf) cancelAnimationFrame(raf);
        apply();
        el.classList.remove('resizing');
        useWm.getState().setBounds(id, next);
      };
      handle.addEventListener('pointermove', move);
      handle.addEventListener('pointerup', up);
      handle.addEventListener('pointercancel', up);
    },
    [id, meta.minW, meta.minH],
  );

  if (!win) return null;
  const Body = APP_COMPONENTS[id];
  const style: React.CSSProperties = win.maximized
    ? { left: 0, top: 0, width: '100%', height: '100%', zIndex: win.z }
    : { left: win.bounds.x, top: win.bounds.y, width: win.bounds.w, height: win.bounds.h, zIndex: win.z };
  const cls = ['win', meta.dark && 'dark', focused && 'focused', win.maximized && 'maximized', hidden && 'minimized'].filter(Boolean).join(' ');
  const s = useWm.getState();

  return (
    <section
      ref={ref}
      className={cls}
      style={style}
      role="dialog"
      aria-label={meta.title}
      aria-hidden={hidden || undefined}
      data-win={id}
      tabIndex={-1}
      onPointerDownCapture={() => s.focus(id)}
      onFocusCapture={() => !focused && s.focus(id)}
    >
      <header className="titlebar" onPointerDown={onTitlePointerDown} onDoubleClick={(e) => !(e.target as HTMLElement).closest('button') && s.toggleMaximize(id)}>
        <button className="tb-btn tb-back" aria-label="Back to desktop" onClick={() => s.minimize(id)} style={{ width: 30 }}>
          <span aria-hidden style={{ fontWeight: 800 }}>
            ‹
          </span>
        </button>
        <span className="tb-icon">
          <AppIcon name={meta.icon} size={20} />
        </span>
        <span className="tb-title">{meta.title}</span>
        {busy && <span className="tb-busy" title="Work is running" />}
        <span className="tb-btns">
          <button className="tb-btn min" aria-label="Minimize" title="Minimize" onClick={() => s.minimize(id)} />
          <button
            className="tb-btn max"
            aria-label={win.maximized ? 'Restore' : 'Maximize'}
            title={win.maximized ? 'Restore' : 'Maximize'}
            onClick={() => s.toggleMaximize(id)}
          />
          <button className="tb-btn close" aria-label="Close" title="Close" onClick={() => s.requestClose(id)} />
        </span>
      </header>
      <div className="win-body">
        <Boundary title={meta.title}>
          <Suspense fallback={<div className="pad muted">Loading {meta.title}…</div>}>
            <Body arg={win.arg} argSeq={win.argSeq} />
          </Suspense>
        </Boundary>
      </div>
      {!win.maximized && EDGES.map((e) => <div key={e} className={`rz rz-${e}`} onPointerDown={onResizeDown(e)} aria-hidden />)}
      {win.confirmClose && <CloseDialog id={id} />}
    </section>
  );
}

function CloseDialog({ id }: { id: AppId }) {
  const procs = activeProcessesFor(id);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => btn.current?.focus(), []);
  const s = useWm.getState();
  if (hasUnsavedChanges(id)) return <div className="win-dialog-scrim" onKeyDown={e=>{if(e.key==='Escape')s.cancelClose(id);}}><div className="dialog" role="alertdialog" aria-labelledby={`cd-${id}`}><h2 id={`cd-${id}`}>Keep your changes?</h2><p>You have unsaved changes in {APPS[id].title}. Save them before closing, or discard this draft.</p><div className="row"><button ref={btn} className="btn primary" onClick={()=>s.cancelClose(id)}>Keep editing</button><button className="btn danger" onClick={()=>s.close(id)}>Discard and close</button></div></div></div>;
  return (
    <div className="win-dialog-scrim" onKeyDown={(e) => e.key === 'Escape' && s.cancelClose(id)}>
      <div className="dialog" role="alertdialog" aria-labelledby={`cd-${id}`}>
        <h2 id={`cd-${id}`}>{procs.map((p) => p.name).join(' and ') || 'Work'} is still running</h2>
        <p>
          Stop it and close the window, or keep it running in the background. A background process stays in the taskbar tray and in
          taskmgr.
        </p>
        <div className="row">
          <button
            className="btn danger"
            onClick={async () => {
              await Promise.allSettled(activeProcessesFor(id).map((p) => p.stop()));
              s.close(id);
            }}
          >
            Stop and close
          </button>
          <button ref={btn} className="btn primary" onClick={() => s.close(id)}>
            Keep running
          </button>
          <button className="btn ghost" onClick={() => s.cancelClose(id)}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export const Window = memo(WindowFrame);
